import React from "react";
import * as Yup from "yup";
import Icon from "assets/icons";
import Reducer from "./reducer";
import { FormikValues } from "formik";
import Variant from "shared/enums/Variant";
import { CouponCodeViewEnum } from "./enum";
import Validation from "shared/form/Validation";
import { alphaNumeric } from "shared/utils/regex";
import { useCopies } from "shared/hooks/useCopies";
import { useValidateCouponCode } from "./dataloader";
import { getCouponCodeApplicableView } from "./utils";
import InventoryType from "shared/enums/InventoryType";
import { useCommonFeatures } from "context/commonFeatures";
import { useReducerWrapper } from "shared/hooks/useReducerWrapper";
import AnalyticsDatastoreType from "shared/enums/AnalyticsDatastoreType";
import { MAX_COUPON_CODE_LENGTH, MIN_COUPON_CODE_LENGTH } from "shared/utils/constants";
import { DispatchInterface, CouponCodePropTypes, State, CouponCodeService } from "./types";
import {
  AnalyticsActionNameEnum,
  AnalyticsContextEntityTypeEnum,
  AnalyticsEventNameEnum,
  AnalyticsScreenNameEnum
} from "shared/enums/AnalyticsEnums";

function useCouponCodeService(props: CouponCodePropTypes): CouponCodeService {
  const copies = useCopies();
  const { initConfirmationAlert } = useCommonFeatures();
  const { mutateAsync: validateCouponCode } = useValidateCouponCode();
  const [state, dispatch] = useReducerWrapper<State, DispatchInterface>(Reducer, props);

  const formHelper = React.useMemo(() => {
    const initialValues = { couponCode: props.purchaseOrderLine.couponCode ?? "" };

    const validationSchema = Yup.object().shape({
      couponCode: Validation.textFieldValidation({
        copies,
        required: true,
        regex: alphaNumeric,
        regexMsg: copies.get("alphanumeric.allowed"),
        min: MIN_COUPON_CODE_LENGTH,
        max: MAX_COUPON_CODE_LENGTH,
        minCharactersCopyKey: "minimum.characters.without.spaces",
        maxCharactersCopyKey: "maximum.characters.without.spaces"
      })
    });
    return { initialValues, validationSchema };
  }, [copies, props.purchaseOrderLine.couponCode]);

  const couponCodeViewChangeHandler = React.useCallback(
    (view: CouponCodeViewEnum) => {
      dispatch.setApplicableView(view);
    },
    [dispatch]
  );

  React.useEffect(() => {
    const applicableView = getCouponCodeApplicableView({
      showCouponCode: props.showCouponCode,
      disableCouponActions: props.disableCouponActions,
      purchaseOrderLine: props.purchaseOrderLine
    });
    couponCodeViewChangeHandler(applicableView);
  }, [props.showCouponCode, props.disableCouponActions, props.purchaseOrderLine, couponCodeViewChangeHandler]);

  const editClickHandler = React.useCallback(() => {
    couponCodeViewChangeHandler(CouponCodeViewEnum.COUPON_CODE_INPUT);
  }, [couponCodeViewChangeHandler]);

  const noPayableAmountMessage = React.useMemo(() => {
    switch (props.purchaseOrderLine.lineType) {
      case InventoryType.TICKET:
      case InventoryType.TICKET_CHANGE:
      case InventoryType.TICKET_UPGRADE:
        return copies.get("coupon.code.at.checkout.ticket.zero.amount.applicable.confirmation.message");
      case InventoryType.BULK_TICKET:
        return copies.get("coupon.code.at.checkout.tickets.zero.amount.applicable.confirmation.message");
      case InventoryType.MEETING:
      case InventoryType.BUNDLED_MEETING:
        return copies.get("coupon.code.at.checkout.meetings.zero.amount.applicable.confirmation.message");
      case InventoryType.ADD_ONS:
        return copies.get("coupon.code.at.checkout.addon.zero.amount.applicable.confirmation.message");
      case InventoryType.UNSCHEDULED_MEETING:
      case InventoryType.BUNDLED_UNSCHEDULED_MEETING:
        return copies.get("coupon.code.at.checkout.unscheduled.meetings.zero.amount.applicable.confirmation.message");
    }
  }, [copies, props.purchaseOrderLine.lineType]);

  const applyCouponSubmitHandler = React.useCallback(
    async (values: FormikValues) => {
      if (values.couponCode === props.purchaseOrderLine.couponCode) {
        couponCodeViewChangeHandler(CouponCodeViewEnum.COUPON_APPLIED);
        return;
      }
      if (!props.registrationParticipantId) return;
      try {
        const data = await validateCouponCode({
          registrationParticipantId: props.registrationParticipantId,
          originatingFormId: props.purchaseOrderLine.originatingEntityId,
          inventoryType: props.purchaseOrderLine.lineType,
          couponCode: values.couponCode,
          quantity: props.purchaseOrderLine.quantity
        });
        if (data.zero)
          initConfirmationAlert({
            message: (
              <div>
                <Icon.Confetti size={60} />
                <div>{noPayableAmountMessage}</div>
              </div>
            ),
            primaryButtonText: copies.get("ok"),
            primaryButtonVariant: Variant.SUCCESS,
            secondaryButtonText: copies.get("cancel"),
            handleConfirm: () => {
              props.applyCouponCodeHandler?.(
                () => couponCodeViewChangeHandler(CouponCodeViewEnum.COUPON_APPLIED),
                values.couponCode
              );
            }
          });
        else
          props.applyCouponCodeHandler?.(
            () => couponCodeViewChangeHandler(CouponCodeViewEnum.COUPON_APPLIED),
            values.couponCode
          );
      } catch (e) {
      } finally {
        props.logEvent?.(
          AnalyticsEventNameEnum.CLICK,
          {
            screenType: AnalyticsScreenNameEnum.COUPON_AT_CHECKOUT,
            action: AnalyticsActionNameEnum.ADD,
            contextEntityType:
              AnalyticsContextEntityTypeEnum[
                props.purchaseOrderLine.lineType as keyof typeof AnalyticsContextEntityTypeEnum
              ]
          },
          AnalyticsDatastoreType.MIXPANEL_INTERNAL
        );
      }
    },
    [copies, couponCodeViewChangeHandler, initConfirmationAlert, noPayableAmountMessage, props, validateCouponCode]
  );

  const cancelEditHandler = React.useCallback(() => {
    couponCodeViewChangeHandler(
      props.purchaseOrderLine.couponCode ? CouponCodeViewEnum.COUPON_APPLIED : CouponCodeViewEnum.COUPON_APPLICABLE
    );
  }, [couponCodeViewChangeHandler, props.purchaseOrderLine.couponCode]);

  const deleteClickHandler = React.useCallback(() => {
    initConfirmationAlert({
      message: copies.get("coupon.code.at.checkout.remove.confirm.msg"),
      handleConfirm: () => {
        props.applyCouponCodeHandler?.(() => couponCodeViewChangeHandler(CouponCodeViewEnum.COUPON_APPLICABLE));
        props.logEvent?.(
          AnalyticsEventNameEnum.CLICK,
          {
            screenType: AnalyticsScreenNameEnum.COUPON_AT_CHECKOUT,
            action: AnalyticsActionNameEnum.REMOVE,
            contextEntityType:
              AnalyticsContextEntityTypeEnum[
                props.purchaseOrderLine.lineType as keyof typeof AnalyticsContextEntityTypeEnum
              ]
          },
          AnalyticsDatastoreType.MIXPANEL_INTERNAL
        );
      }
    });
  }, [copies, couponCodeViewChangeHandler, initConfirmationAlert, props]);

  return {
    state,
    formHelper,
    editClickHandler,
    cancelEditHandler,
    deleteClickHandler,
    applyCouponSubmitHandler,
    couponCodeViewChangeHandler
  };
}

export default useCouponCodeService;
